import css from './Avatar.module.css'

interface AvatarProps {
    title: string
    // Kept for call-site compatibility; the avatar color is derived from `title`
    // (always present, same for a user everywhere) so it's consistent across
    // surfaces — `color` isn't passed by every call site.
    color?: string
    avatar?: string | null
    size?: 'small' | 'medium' | 'medium2' | 'big'
}

// Default (image-less) avatars get one of these background colors, chosen
// deterministically from the title so a given person is always the same color.
// The initial is a darkened tone of that same background (no stroke) — SCD-124.
const AVATAR_COLORS = ['#EAD159', '#C8B8EF', '#BBB1A0', '#82A88D', '#D18B5E']

const pickColor = (key: string): string => {
    let hash = 0
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0
    return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

// Darken a #rrggbb toward black by `factor` — used to tint the initial a deeper
// shade of its own background.
const darken = (hex: string, factor: number): string => {
    const n = parseInt(hex.slice(1), 16)
    const r = Math.round(((n >> 16) & 255) * factor)
    const g = Math.round(((n >> 8) & 255) * factor)
    const b = Math.round((n & 255) * factor)
    return `rgb(${r}, ${g}, ${b})`
}

const Avatar = ({ title, avatar = null, size = 'small' }: AvatarProps) => {
    const initial = title.charAt(0).toUpperCase()

    const avatarClassName = [
        css.avatar,
        size === 'medium' && css.avatar_medium,
        size === 'medium2' && css.avatar_medium2,
        size === 'big' && css.avatar_big,
        avatar && css.avatar_image
    ].filter(Boolean).join(' ')

    const bg = pickColor(title)
    const style = avatar ? undefined : { background: bg, color: darken(bg, 0.5) }

    return (
        <div className={avatarClassName} style={style}>
            {avatar ? (
                <img src={avatar} alt={title} className={css.image} />
            ) : (
                <span className={css.initial}>{initial}</span>
            )}
        </div>
    )
}

export default Avatar
